import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/base/PageHeader";
import SectionTitle from "@/components/base/SectionTitle";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import { siteInfo, testimonials } from "@/mocks/site";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "我们做什么",
    desc: "把网暴黑料当作公共议题来研究：梳理事件脉络、归纳施暴者动机、还原组织化操作的链路，并把成果整理成可读、可核验的档案。",
    tone: "primary",
  },
  {
    icon: "ri-forbid-2-line",
    title: "我们不做什么",
    desc: "不做隐私曝光，不传播未经核实的指控，不站队骂人，也不以受害者的痛苦换取流量。涉及具体个人的内容，我们只引用权威渠道已公开的信息。",
    tone: "secondary",
  },
  {
    icon: "ri-team-line",
    title: "我们为谁而做",
    desc: "被网暴过的人、担心被网暴的人，以及所有不想在无意中成为帮凶的人。我们相信，理解机制是减少伤害的第一步。",
    tone: "accent",
  },
];

const principles = [
  {
    icon: "ri-file-search-line",
    title: "先核实，再表达",
    desc: "任何结论都必须能找到公开来源。无法核实的说法，宁可不用，也不做模糊化处理。",
  },
  {
    icon: "ri-scales-3-line",
    title: "区分现象与个案",
    desc: "我们讨论的是机制与规律，不对未被权威机构认定的具体个人作出事实判断。",
  },
  {
    icon: "ri-emotion-line",
    title: "克制情绪表达",
    desc: "愤怒容易传播，但很难说清问题。我们尽量让文本冷静下来，把空间留给分析。",
  },
  {
    icon: "ri-user-heart-line",
    title: "优先保护当事人",
    desc: "在呈现事实与保护隐私之间，我们始终倾向后者，尤其是涉及未成年人时。",
  },
  {
    icon: "ri-refresh-line",
    title: "允许被修正",
    desc: "如果内容出现错误，我们会更正并保留修改说明，而不是悄悄删改。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "提供可用的出口",
    desc: "指出问题之外，更要给出可执行的建议：如何取证、如何举报、如何寻求专业支持。",
  },
];

const toneMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  secondary: "bg-secondary-100 text-secondary-800",
  accent: "bg-accent-100 text-accent-800",
};

export default function AboutPage() {
  usePageMeta({
    title: `关于我们｜网暴黑料调查站的定位与内容原则 - ${siteInfo.name}`,
    description:
      "黑料显微镜是一个专注网暴黑料调查与舆论解剖的内容站。我们介绍自己的品牌定位、发展理念与六条内容原则，也欢迎读者来信与线索投递。",
    keywords: "关于黑料显微镜,网暴黑料调查,内容原则,网站定位,舆论解剖",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          url: `${SITE_ORIGIN}/about`,
          name: `关于我们 - ${siteInfo.name}`,
          inLanguage: "zh-CN",
          description: siteInfo.description,
          mainEntity: {
            "@type": "Organization",
            name: siteInfo.name,
            url: SITE_ORIGIN,
            email: siteInfo.email,
            description: siteInfo.description,
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
            { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
          ],
        },
      ],
    },
  });

  return (
    <Layout>
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
        eyebrow="About Us"
        title="关于黑料显微镜"
        description="一个不追热度的调查站。我们想做的，是把网暴黑料从谈资变成可以被理解、被讨论、被预防的公共议题。"
      />

      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <SectionTitle
                eyebrow="Brand"
                title="为什么叫「显微镜」"
                description="因为大部分网暴的真相，藏在细节里。一句话被截断的位置、账号注册的时间、评论出现的密度——把这些细节摆在一起，很多看起来无解的事就有了答案。"
              />
              <div className="mt-6 space-y-4 text-sm md:text-base leading-relaxed text-foreground-600">
                <p>
                  我们做这个站点的起点很简单：身边有人被网暴，却发现几乎找不到一份讲得清楚的资料。要么是情绪宣泄，要么是碎片化的新闻，很少有人愿意把前因后果完整地写下来。
                </p>
                <p>
                  于是我们开始整理档案。每一份档案都从公开信息出发，遵循同一套方法：先还原时间线，再分析动机，然后看背后的组织与利益，最后落到普通人能做的事。
                </p>
                <p className="text-foreground-700 font-medium">
                  {siteInfo.name}不提供结论，只提供一条能自己走通的分析路径。
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Dark%20editorial%20still%20life%20of%20an%20investigator%20desk%20with%20stacked%20case%20files%2C%20a%20magnifying%20glass%2C%20handwritten%20notes%20and%20a%20warm%20amber%20desk%20lamp%20glow%2C%20charcoal%20tones%20with%20crimson%20accents%2C%20grainy%20documentary%20photography&width=1200&height=900&seq=wbh-about-01&orientation=landscape"
                  alt="调查工作台上的档案、放大镜与笔记，象征逐条核对公开信息的调查过程"
                  title="黑料显微镜调查方式 · 网暴黑料调查"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <SectionTitle
            eyebrow="Positioning"
            title="网站定位：边界比立场更重要"
            description="我们相信，一个做舆论议题的站点，先要把自己不做的事情说清楚。"
          />
          <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
            {positioning.map((item) => (
              <div
                key={item.title}
                className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200"
              >
                <span
                  className={`w-12 h-12 flex items-center justify-center rounded-md ${toneMap[item.tone]}`}
                >
                  <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-foreground-950 py-14 md:py-20">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <SectionTitle
            eyebrow="Principles"
            title="六条内容原则"
            description="这些原则不是装饰，而是我们决定一篇稿子能不能发出去的标准。"
            light
          />
          <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {principles.map((item, index) => (
              <div
                key={item.title}
                className="p-5 md:p-6 rounded-lg bg-foreground-900 border border-foreground-800"
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <span className="font-heading text-xl font-black text-foreground-700">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-base md:text-lg font-bold text-background-50">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <SectionTitle
            eyebrow="Readers"
            title="读者口碑"
            description="来自读者来信与投稿的反馈，我们保留原意，仅做必要的隐私处理。"
          />
          <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true"></i>
                <blockquote className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-900 font-heading text-sm font-bold shrink-0">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900 truncate">
                      {item.name}
                    </span>
                    <span className="block text-xs text-foreground-500 truncate">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-secondary-50 border-t border-secondary-200 py-14 md:py-16">
        <div className="max-w-content mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionTitle
                eyebrow="Contact"
                title="联系我们"
                description="无论是读者来信、线索投递还是内容合作，都欢迎通过下面的方式找到我们。"
              />
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-lg bg-background-50 border border-secondary-200">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  一般咨询
                </h3>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-1.5 block text-sm text-foreground-600 hover:text-primary-600 transition-colors break-all"
                >
                  {siteInfo.email}
                </a>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-secondary-200">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-lightbulb-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                  线索投递
                </h3>
                <a
                  href={`mailto:${siteInfo.tipEmail}`}
                  className="mt-1.5 block text-sm text-foreground-600 hover:text-primary-600 transition-colors break-all"
                >
                  {siteInfo.tipEmail}
                </a>
              </div>
              <div className="sm:col-span-2 p-5 rounded-lg bg-background-50 border border-secondary-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    工作时间
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600">{siteInfo.workTime}</p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors shrink-0"
                >
                  <i className="ri-mail-send-line" aria-hidden="true"></i>
                  前往联系页面
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}